import { MAX_RESOURCE_BYTES, assetKind } from '@agentic-ivy/shared-types';
import type { FootageLinkKind } from './links';

export interface FootageUploadProgress {
  phase: 'uploading' | 'resuming' | 'completing';
  sentBytes: number;
  totalBytes: number;
}

export interface FootageUploadClient {
  /** Direct-to-storage resumable multipart upload of one file; resolves once the server has completed it. */
  uploadFile(args: {
    conversationId: string;
    file: File;
    onProgress: (progress: FootageUploadProgress) => void;
    signal: AbortSignal;
  }): Promise<{ sourceId: string }>;
  /** Asks the server to fetch a pasted link into storage (the worker's import job). */
  importLink?(args: { conversationId: string; url: string; kind: FootageLinkKind }): Promise<{ sourceId: string }>;
}

/** The attachment route (POST /upload) passes bytes through the server and stops here. */
const ATTACHMENT_ROUTE_MAX_BYTES = 100 * 1024 * 1024;

/**
 * A file that goes straight to storage: heavy kinds, and anything over 50 MB, which on the attachment route
 * would get no processing and could time out. Audio stays on that route to its limit, because the resumable
 * route treats audio as footage.
 */
export function needsResumableUpload(file: { name: string; type: string; size: number }): boolean {
  const kind = assetKind(file.name, file.type);
  if (kind === 'video' || kind === 'design' || kind === 'font') return true;
  return file.size > (kind === 'audio' ? ATTACHMENT_ROUTE_MAX_BYTES : MAX_RESOURCE_BYTES);
}

let client: FootageUploadClient | null = null;

export function registerFootageUploadClient(next: FootageUploadClient | null): void {
  client = next;
}

export function getFootageUploadClient(): FootageUploadClient | null {
  return client;
}
